import { useScriptTag } from '@vueuse/core';
import { createError, useRoute, useRuntimeConfig } from 'nuxt/app';
import { onMounted } from 'vue';
import { useCountryStore } from '../stores/countryStore';
import { useApi } from '../utils/useApi';

const RECAPTCHA_ENABLED = true; // Set to false to disable reCAPTCHA functionality
const TOKEN_HEADER = 'Recaptcha-Token';
const PROVIDER_HEADER = 'Recaptcha-Provider';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TENCENT_SCRIPT_URL = 'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js';

// Tencent's international SDK (TJNCaptcha-global.js) injects a floating
// "Privacy · Terms" brand badge in the bottom-right corner. The selectors
// below cover the common id/class patterns the SDK uses; if the SDK changes
// or the badge is still visible, inspect the DOM and add the actual class.
// NOTE: Tencent's TOS may require an alternative disclosure if the badge is
// hidden — see the privacy policy / captcha disclosure plan.
const TENCENT_BRAND_HIDE_STYLE_ID = 'tencent-captcha-dy__terms_id';
const TENCENT_BRAND_HIDE_CSS = `
[id*="tcapt"][id*="brand"],
[class*="tcapt"][class*="brand"],
[class*="tcaptcha-logo"],
[class*="tcapt-logo"],
[class*="tcapt-footer"] {
	display: none !important;
}
`;

function hideTencentBranding(): void {
	if (typeof document === 'undefined') {
		return;
	}
	if (document.getElementById(TENCENT_BRAND_HIDE_STYLE_ID)) {
		return;
	}
	const style = document.createElement('style');
	style.id = TENCENT_BRAND_HIDE_STYLE_ID;
	style.textContent = TENCENT_BRAND_HIDE_CSS;
	document.head.appendChild(style);
}

// Countries where Cloudflare/Turnstile is commonly blocked or unreliable.
// Users from these regions get the Tencent fallback if Turnstile fails to load.
// All other users get Turnstile-only (no fallback) to avoid unnecessary
// third-party data processing — see privacy policy / GDPR considerations.
const TURNSTILE_RESTRICTED_COUNTRIES = new Set(['CN', 'RU', 'IR', 'SY', 'CU', 'KP']);

type Provider = 'turnstile' | 'tencent';

export function useRecaptcha() {
	const { turnstileSiteKey, tencentAppId } = (useRuntimeConfig().public.captcha || {}) as CaptchaRuntimeConfig;

	const $api = useApi();

	function forceCaptchaFallback(): boolean {
		try {
			const route = useRoute();
			return route?.query?.captchaFallback !== undefined;
		}
		catch {
			return false;
		}
	}

	async function isTurnstileRestrictedRegion(): Promise<boolean> {
		try {
			const countryStore = useCountryStore();
			// initDefaultCountry is idempotent — re-entry after success or
			// during an in-flight call is a no-op, so this is safe even when
			// the host app already calls it from its root layout.
			await countryStore.initDefaultCountry();
			return TURNSTILE_RESTRICTED_COUNTRIES.has(countryStore.defaultCountryCode);
		}
		catch {
			// Geo lookup failed — fail closed (treat as global) to honor the
			// privacy intent of not loading Tencent for non-restricted users.
			return false;
		}
	}

	function getTurnstile(): Turnstile | undefined {
		return (window as { turnstile?: Turnstile }).turnstile;
	}

	function getTencent(): TencentCaptchaCtor | undefined {
		return (window as { TencentCaptcha?: TencentCaptchaCtor }).TencentCaptcha;
	}

	function activeProvider(): Provider | null {
		if (getTurnstile()) {
			return 'turnstile';
		}
		if (getTencent()) {
			return 'tencent';
		}
		return null;
	}

	async function getTurnstileToken(action: string): Promise<string> {
		const turnstile = getTurnstile();
		if (!turnstile) {
			throw createError({
				statusCode: 400,
				statusMessage: 'RECAPTCHA_NOT_LOADED: turnstile object missing from window',
				message: 'Recaptcha could not be loaded. Please refresh the page and try again. [ERR_RC_LOAD]',
			});
		}

		return new Promise<string>((resolve, reject) => {
			const container = document.createElement('div');
			container.style.position = 'absolute';
			container.style.visibility = 'hidden';
			container.style.pointerEvents = 'none';
			container.style.width = '0';
			container.style.height = '0';
			document.body.appendChild(container);

			let widgetId: string | undefined;
			let settled = false;

			const cleanup = () => {
				try {
					if (widgetId !== undefined) {
						turnstile.remove(widgetId);
					}
				}
				catch { /* widget already gone */ }
				container.remove();
			};
			const succeed = (token: string) => {
				if (settled) {
					return;
				}
				settled = true;
				cleanup();
				resolve(token);
			};
			const fail = (cause: unknown, code: string, statusMessage: string) => {
				if (settled) {
					return;
				}
				settled = true;
				cleanup();
				reject(createError({
					statusCode: 400,
					statusMessage: `${statusMessage} action="${action}"`,
					message: `Recaptcha verification failed. Please refresh the page and try again. [${code}]`,
					cause,
				}));
			};

			try {
				widgetId = turnstile.render(container, {
					'sitekey': turnstileSiteKey as string,
					action,
					'execution': 'execute',
					'appearance': 'execute',
					'callback': succeed,
					'error-callback': error => fail(error, 'ERR_RC_EXEC', 'RECAPTCHA_EXECUTE_FAILED:'),
					'expired-callback': () => fail(null, 'ERR_RC_EXPIRED', 'RECAPTCHA_TOKEN_EXPIRED:'),
					'timeout-callback': () => fail(null, 'ERR_RC_TIMEOUT', 'RECAPTCHA_TIMEOUT:'),
				});

				if (widgetId === undefined) {
					fail(null, 'ERR_RC_RENDER', 'RECAPTCHA_RENDER_FAILED:');
					return;
				}

				turnstile.execute(widgetId);
			}
			catch (error) {
				fail(error, 'ERR_RC_RENDER', 'RECAPTCHA_RENDER_THREW:');
			}
		});
	}

	async function getTencentToken(action: string): Promise<string> {
		const TencentCaptcha = getTencent();
		if (!TencentCaptcha) {
			throw createError({
				statusCode: 400,
				statusMessage: 'RECAPTCHA_NOT_LOADED: TencentCaptcha missing from window',
				message: 'Recaptcha could not be loaded. Please refresh the page and try again. [ERR_TC_LOAD]',
			});
		}
		if (!tencentAppId) {
			throw createError({
				statusCode: 400,
				statusMessage: `TENCENT_NO_APPID: runtime config tencentAppId missing (action="${action}")`,
				message: 'Recaptcha could not be loaded. Please refresh the page and try again. [ERR_TC_NO_APPID]',
			});
		}

		return new Promise<string>((resolve, reject) => {
			const trigger = document.createElement('div');
			trigger.style.position = 'absolute';
			trigger.style.visibility = 'hidden';
			trigger.style.pointerEvents = 'none';
			trigger.style.width = '0';
			trigger.style.height = '0';
			document.body.appendChild(trigger);

			let settled = false;
			const cleanup = () => {
				trigger.remove();
			};
			const fail = (cause: unknown, code: string, statusMessage: string) => {
				if (settled) {
					return;
				}
				settled = true;
				cleanup();
				reject(createError({
					statusCode: 400,
					statusMessage: `${statusMessage} action="${action}"`,
					message: `Recaptcha verification failed. Please refresh the page and try again. [${code}]`,
					cause,
				}));
			};

			try {
				const captcha = new TencentCaptcha(trigger, tencentAppId as string, (res) => {
					if (settled) {
						return;
					}
					if (res.ret === 0 && res.ticket && res.randstr) {
						if (res.ticket.startsWith('trerror_')) {
							fail(res, 'ERR_TC_CLIENT', `TENCENT_CAPTCHA_CLIENT_ERROR: ticket=${res.ticket}`);
							return;
						}
						settled = true;
						cleanup();
						resolve(JSON.stringify({
							ticket: res.ticket,
							randstr: res.randstr,
							appId: res.CaptchaAppId ?? tencentAppId,
							action,
						}));
					}
					else if (res.ret === 2) {
						fail(null, 'ERR_TC_CANCELED', 'TENCENT_CAPTCHA_CANCELED:');
					}
					else {
						fail(res, 'ERR_TC_FAIL', `TENCENT_CAPTCHA_FAILED: ret=${res.ret}`);
					}
				}, { bizState: action, needFeedBack: false });
				captcha.show();
			}
			catch (error) {
				fail(error, 'ERR_TC_THREW', 'TENCENT_CAPTCHA_THREW:');
			}
		});
	}

	async function getRecaptchaToken(action: string): Promise<{ token: string; provider: Provider }> {
		const provider = activeProvider();
		if (!provider) {
			throw createError({
				statusCode: 400,
				statusMessage: `RECAPTCHA_NOT_LOADED: no captcha provider available (action="${action}")`,
				message: 'Recaptcha could not be loaded. Please refresh the page and try again. [ERR_RC_LOAD]',
			});
		}
		const token = provider === 'turnstile' ? await getTurnstileToken(action) : await getTencentToken(action);
		return { token, provider };
	}

	return {
		async apiCallWithRecaptcha<T>(request: Parameters<typeof $api>[0], options?: Parameters<typeof $api<T>>[1], action: string = 'submit') {
			if (!activeProvider()) {
				throw createError({
					statusCode: 400,
					statusMessage: `RECAPTCHA_MISSING: no captcha provider on window before API call (action="${action}", endpoint="${String(request)}")`,
					message: 'Recaptcha could not be loaded. Please refresh the page and try again. [ERR_RC_MISSING]',
				});
			}

			const headers = {
				...options?.headers,
			};
			if (RECAPTCHA_ENABLED) {
				const { token, provider } = await getRecaptchaToken(action);
				(headers as Record<string, string>)[TOKEN_HEADER] = token;
				(headers as Record<string, string>)[PROVIDER_HEADER] = provider;
			}

			return $api<T>(request, {
				...options,
				headers,
			});
		},
		/**
		 * Loads Cloudflare Turnstile. For users in regions where Turnstile is
		 * commonly blocked (CN, RU, IR, …), Tencent Captcha is loaded as a
		 * fallback if Turnstile fails. Other users get Turnstile only — no
		 * Tencent — to avoid the EU→China data transfer for non-restricted users.
		 */
		loadRecaptcha() {
			const turnstileScript = useScriptTag(TURNSTILE_SCRIPT_URL, () => {

			}, {
				async: true,
				defer: true,
				crossOrigin: undefined,
				manual: true,
			});
			const tencentScript = useScriptTag(TENCENT_SCRIPT_URL, () => {

			}, {
				async: true,
				defer: true,
				crossOrigin: undefined,
				manual: true,
			});

			onMounted(async () => {
				if (activeProvider()) {
					return;
				}

				const forceFallback = forceCaptchaFallback();
				const tencentFallbackAllowed = forceFallback || await isTurnstileRestrictedRegion();
				const skipTurnstile = forceFallback;

				if (!skipTurnstile) {
					try {
						await turnstileScript.load();
						if (getTurnstile()) {
							return;
						}
					}
					catch {
						console.warn('Failed to load Turnstile script.');
						/* turnstile unreachable — fall through to tencent if allowed */
					}
				}
				else {
					console.warn('captchaFallback query param present; skipping Turnstile and loading Tencent Captcha.');
				}

				if (!tencentFallbackAllowed) {
					console.warn('Turnstile failed and user not in restricted region; no captcha fallback.');
					return;
				}

				try {
					await tencentScript.load();
					hideTencentBranding();
				}
				catch {
					console.warn('Failed to load Tencent Captcha script; both providers unreachable.');
					/* both providers unreachable; getRecaptchaToken will throw on use */
				}
			});
		},
	};
}

interface TurnstileRenderParams {
	'sitekey': string;
	'action'?: string;
	'execution'?: 'render' | 'execute';
	'appearance'?: 'always' | 'execute' | 'interaction-only';
	'callback'?: (token: string) => void;
	'error-callback'?: (error: string) => void;
	'expired-callback'?: () => void;
	'timeout-callback'?: () => void;
}

interface Turnstile {
	render: (container: HTMLElement | string, params: TurnstileRenderParams) => string | undefined;
	execute: (widgetIdOrContainer: HTMLElement | string) => void;
	reset: (widgetIdOrContainer?: HTMLElement | string) => void;
	remove: (widgetIdOrContainer: HTMLElement | string) => void;
	getResponse: (widgetIdOrContainer?: HTMLElement | string) => string | undefined;
}

interface TencentCaptchaResult {
	ret: number;
	ticket?: string;
	randstr?: string;
	CaptchaAppId?: string;
	bizState?: unknown;
	errorCode?: number;
	errorMessage?: string;
}

interface TencentCaptchaOptions {
	bizState?: unknown;
	needFeedBack?: boolean;
}

interface TencentCaptchaInstance {
	show: () => void;
	destroy?: () => void;
}

type TencentCaptchaCtor = new (element: HTMLElement, appId: string, callback: (res: TencentCaptchaResult) => void, options?: TencentCaptchaOptions) => TencentCaptchaInstance;

interface CaptchaRuntimeConfig {
	turnstileSiteKey?: string;
	tencentAppId?: string;
}
